/**
 * Which predicate ref a registration rule is authored against.
 *
 * Mirrors how `build_registration_context` materialises the submitted value:
 * a host-mapped attribute resolves through the `host` entity, everything else
 * through `field:<code_name>`. Kept out of the page component so the choice
 * can be tested on its own - a ref that does not match how the value is
 * actually stored produces a rule that silently never fires.
 */
import type {
  EntityAttributeDefinition,
  EntityTypeDefinition,
} from '@/admin/hooks/use-entities'

/** Host columns `check_duplicates` knows how to query. */
export const DUPLICATE_CHECK_HOST_COLUMNS = new Set([
  'name',
  'registration_number',
  'vin',
  'address',
  'address_line_1',
  'address_postcode',
])

export const LENGTH_PATTERN_FIELD_TYPES = new Set<
  EntityAttributeDefinition['field_type']
>(['TEXT', 'RICH_TEXT', 'EMAIL', 'PHONE', 'LINK', 'ADDRESS'])

/**
 * Field types a duplicate check can be authored on when the attribute is not
 * a host column. `check_duplicates` resolves `field:<code_name>` with an exact
 * match against the stored EAV value, which only answers a useful question for
 * a field that identifies one record. An allowlist, not a denylist: a duplicate
 * rule on a BOOLEAN or SELECT would flag every record sharing the option, and a
 * type added later has to be considered before it can carry one.
 */
export const DUPLICATE_CHECK_FIELD_TYPES = new Set<
  EntityAttributeDefinition['field_type']
>(['TEXT', 'EMAIL', 'PHONE', 'LINK', 'NUMBER'])

function isHostEntity(entity: EntityTypeDefinition) {
  return entity.key === 'property' || entity.key === 'host'
}

function mapsToColumn(attribute: EntityAttributeDefinition) {
  return String(attribute.configuration_data?.maps_to_column ?? '')
}

export function getDuplicateRuleRef(
  entity: EntityTypeDefinition,
  attribute: EntityAttributeDefinition | null | undefined,
): string | null {
  if (!attribute) return null
  if (isHostEntity(entity)) {
    if (attribute.field_type === 'ADDRESS') return 'entity:host.address'
    const column = mapsToColumn(attribute)
    if (column) {
      // A host-mapped attribute is stored on Host, not as an EAV row, so the
      // `field:` fallback below could never match it. Either the column is one
      // `check_duplicates` knows how to query, or this attribute gets no
      // duplicate rule - never a rule that silently finds nothing.
      return DUPLICATE_CHECK_HOST_COLUMNS.has(column)
        ? `entity:host.${column}`
        : null
    }
  }
  // Everything else resolves through `field:<code_name>`, the same ref
  // `getValueRuleRef` uses and the one `check_duplicates` has always
  // supported - without this an identifier held as an EAV attribute (a serial
  // number, an asset tag) could not be given a duplicate rule at all.
  if (DUPLICATE_CHECK_FIELD_TYPES.has(attribute.field_type)) {
    return `field:${attribute.code_name}`
  }
  return null
}

/**
 * Reference used by length / pattern rules. Mirrors how
 * `buildHostRuleEntity` and `build_registration_context` materialise
 * the registration value: host-mapped attributes resolve through the
 * host entity, everything else through `field:<code_name>`.
 */
export function getValueRuleRef(
  entity: EntityTypeDefinition,
  attribute: EntityAttributeDefinition | null | undefined,
): string | null {
  if (!attribute) return null
  if (isHostEntity(entity)) {
    if (attribute.field_type === 'ADDRESS') return 'entity:host.address_line_1'
    const column = mapsToColumn(attribute)
    if (column) return `entity:host.${column}`
  }
  return `field:${attribute.code_name}`
}
